import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Signup from './components/Signup';
import PaymentSuccess from './components/PaymentSuccess';
import UpdateNotification from './components/UpdateNotification';
import CahierDesChargesPublic from './components/CahierDesChargesPublic';
import CahierDesChargesRegie from './components/CahierDesChargesRegie';
import CahierDesChargesDestratificateurs from './components/CahierDesChargesDestratificateurs';
import CahierDesChargesCEE from './components/CahierDesChargesCEE';
import AuditGratuit from './components/AuditGratuit';
import Bornii from './components/Bornii';
import AbonnementSite from './components/AbonnementSite';
import AbonnementSiteApp from './components/AbonnementSiteApp';
import AbonnementSitePwa from './components/AbonnementSitePwa';
import Offres from './components/Offres';
import LeadsCEE from './components/LeadsCEE';
import FormationAssurance from './components/FormationAssurance';
import DossierCandidatAssu from './components/DossierCandidatAssu';
import ConditionsAssu from './components/ConditionsAssu';
import CalculPaie from './components/CalculPaie';
import BoutiqueInfluenceurs from './components/BoutiqueInfluenceurs';
import PrivacyPolicy from './components/PrivacyPolicy';
import ConsentPreferences from './components/ConsentPreferences';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import './App.css';

// Init OneSignal une seule fois (uniquement pour les pages CRM, après auth)
let oneSignalInitialized = false;
function initOneSignalSilent(userId) {
  if (oneSignalInitialized) return;
  oneSignalInitialized = true;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(async function (OneSignal) {
    try {
      await OneSignal.init({
        appId: '9c3a36f3-7b0a-416a-b77e-2dd5c37013a8',
        safari_web_id: 'web.onesignal.auto.37a647a2-1bdc-46a8-a505-4f4cc6400a46',
        // Aucune UI OneSignal par défaut : on gère tout côté React
        notifyButton: { enable: false },
        autoRegister: false,
        autoResubscribe: true,
        promptOptions: { slidedown: { prompts: [] } },
        allowLocalhostAsSecureOrigin: true
      });
      if (userId) {
        try { await OneSignal.login(String(userId)); } catch (e) { console.error('OneSignal.login:', e); }
      }
    } catch (e) {
      console.error('OneSignal init err:', e);
    }
  });
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Helper : décoder l'id user depuis le JWT en localStorage
  const getUserIdFromToken = () => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return null;
      const payload = JSON.parse(atob(t.split('.')[1]));
      return payload && payload.id ? String(payload.id) : null;
    } catch { return null; }
  };

  // Détacher OneSignal de l'utilisateur au logout
  const oneSignalLogout = () => {
    const apply = (OneSignal) => OneSignal.logout().catch(e => console.error('OneSignal.logout:', e));
    if (window.OneSignal && window.OneSignal.User) apply(window.OneSignal);
    else { window.OneSignalDeferred = window.OneSignalDeferred || []; window.OneSignalDeferred.push(apply); }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      // Init OneSignal silencieux + login user (pas de bell, pas de slidedown auto)
      const uid = getUserIdFromToken();
      initOneSignalSilent(uid);
    }
    setLoading(false);

    // Intercepteur axios global : déconnexion auto si le jeton est absent ou invalide.
    // On distingue « non authentifié » de « pas le droit » : un 403 sur une ressource
    // interdite (rôle restreint comme le valideur) ne doit PAS déconnecter, sinon le
    // premier appel refusé renvoie l'utilisateur à l'écran de connexion.
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          const url = error.config?.url || '';
          const message = String(error.response.data?.error || '');
          const jetonInvalide =
            error.response.status === 401 || /token/i.test(message);
          const routePublique =
            url.includes('/login') || url.includes('/signup') || url.includes('/public');

          if (jetonInvalide && !routePublique) {
            localStorage.removeItem('token');
            sessionStorage.clear();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const handleLogin = (token) => {
    // Nettoyer complètement avant de se connecter
    localStorage.clear();
    sessionStorage.clear();

    localStorage.setItem('token', token);
    setIsAuthenticated(true);

    // Init OneSignal (silencieux) + identifier l'utilisateur
    const uid = getUserIdFromToken();
    initOneSignalSilent(uid);

    // Forcer un rechargement complet pour être sûr que le cache est vidé
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 100);
  };

  const handleLogout = () => {
    // Détacher OneSignal de l'utilisateur (il ne recevra plus les push perso)
    oneSignalLogout();
    localStorage.removeItem('token');
    sessionStorage.clear(); // Nettoyer aussi le sessionStorage
    setIsAuthenticated(false);
    // Forcer un rechargement complet pour vider le cache
    window.location.href = '/login';
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <Router>
      {/* Notification de mise à jour disponible pour tous les utilisateurs */}
      <UpdateNotification />

      {/* Popup personnalisé de demande d'autorisation des notifications — uniquement si authentifié */}
      {isAuthenticated && <NotificationPermissionPrompt />}

      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />
          }
        />
        <Route
          path="/signup"
          element={<Signup />}
        />
        <Route
          path="/payment-success"
          element={<PaymentSuccess />}
        />
        <Route
          path="/cahier-des-charges"
          element={<CahierDesChargesPublic />}
        />
        <Route
          path="/cahier-des-charges-regie"
          element={<CahierDesChargesRegie />}
        />
        <Route
          path="/cahier-des-charge"
          element={<CahierDesChargesDestratificateurs />}
        />
        <Route
          path="/cahier-des-charge-cee"
          element={<CahierDesChargesCEE />}
        />
        <Route
          path="/audit-gratuit"
          element={<AuditGratuit />}
        />
        <Route
          path="/abonnement-site"
          element={<AbonnementSite />}
        />
        <Route
          path="/abonnement-site-app"
          element={<AbonnementSiteApp />}
        />
        <Route
          path="/abonnement-site-pwa"
          element={<AbonnementSitePwa />}
        />
        <Route
          path="/bornii"
          element={<Bornii />}
        />
        <Route
          path="/offres"
          element={<Offres />}
        />
        <Route
          path="/leads-cee"
          element={<LeadsCEE />}
        />
        <Route
          path="/formation-assurance"
          element={<FormationAssurance />}
        />
        <Route
          path="/dossier-candidat/:token"
          element={<DossierCandidatAssu />}
        />
        <Route
          path="/conditions-assurance"
          element={<ConditionsAssu />}
        />
        {/* Adresses courtes des liens à envoyer : /c/<clé> et /p/<clé>.
            La clé est retirée de la barre d'adresse dès l'ouverture. */}
        <Route
          path="/c/:cle"
          element={<ConditionsAssu />}
        />
        <Route
          path="/calcul-paie"
          element={<CalculPaie />}
        />
        <Route
          path="/p/:cle"
          element={<CalculPaie />}
        />
        <Route
          path="/boutique-influenceurs"
          element={<BoutiqueInfluenceurs />}
        />
        <Route
          path="/privacy-policy"
          element={<PrivacyPolicy />}
        />
        <Route
          path="/consent-preferences"
          element={<ConsentPreferences />}
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? <Dashboard onLogout={handleLogout} /> : <Navigate to="/login" />
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Router>
  );
}

export default App;
